import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';
import {
  InsurerAttentionInitResponse,
  InsurerAttentionSearchResponse,
  InsurerAttentionDetailResponse,
  InsurerAttentionSearchFilters,
} from '@/pages/main/insurerAttention/types';

// ==================== API Service Functions ====================

/**
 * Get initialization data (prefectures)
 */
const getInit = async (
  siteType: string,
  clinicNumber: string
): Promise<InsurerAttentionInitResponse> => {
  const response = await apiClient.get<InsurerAttentionInitResponse>(
    clinicApiPath(siteType, clinicNumber, '/insurer-attentions/init')
  );
  return response.data;
};

/**
 * Search insurer attentions with filters
 */
const search = async (
  siteType: string,
  clinicNumber: string,
  filters: InsurerAttentionSearchFilters
): Promise<InsurerAttentionSearchResponse> => {
  // Clean up empty values
  const params: Record<string, string | number> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value;
    }
  });

  const response = await apiClient.get<InsurerAttentionSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/insurer-attentions/search'),
    { params }
  );
  return response.data;
};

/**
 * Get insurer attention detail by ID
 */
const getDetail = async (
  siteType: string,
  clinicNumber: string,
  id: number
): Promise<InsurerAttentionDetailResponse> => {
  const response = await apiClient.get<InsurerAttentionDetailResponse>(
    clinicApiPath(siteType, clinicNumber, `/insurer-attentions/${id}`)
  );
  return response.data;
};

/**
 * Get all insurer attentions (paginated)
 */
const getAll = async (
  siteType: string,
  clinicNumber: string,
  perPage: number = 20,
  page: number = 1
): Promise<InsurerAttentionSearchResponse> => {
  const response = await apiClient.get<InsurerAttentionSearchResponse>(
    clinicApiPath(siteType, clinicNumber, '/insurer-attentions'),
    { params: { per_page: perPage, page } }
  );
  return response.data;
};

/**
 * Download attachment file
 */
const downloadAttachment = async (
  siteType: string,
  clinicNumber: string,
  attachment: string,
  id: number,
  filename: string
): Promise<void> => {
  const response = await apiClient.get(
    clinicApiPath(siteType, clinicNumber, `/insurer-attentions/download/${attachment}/${id}`),
    { responseType: 'blob' }
  );

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ==================== Hooks ====================

/**
 * Hook to get insurer attention initialization data
 */
export const useMainInsurerAttentionInit = () => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['insurer-attentions', 'init', tenant, clinicNumber],
    queryFn: () => getInit(tenant, clinicNumber),
    enabled: !!tenant && !!clinicNumber,
  });
};

/**
 * Hook to search insurer attentions
 */
export const useMainInsurerAttentionSearch = (filters: InsurerAttentionSearchFilters | null) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['insurer-attentions', 'search', tenant, clinicNumber, filters],
    queryFn: () => search(tenant, clinicNumber, filters!),
    enabled: !!tenant && !!clinicNumber && !!filters,
  });
};

/**
 * Hook to get insurer attention detail
 */
export const useMainInsurerAttentionDetail = (id: number) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['insurer-attentions', 'detail', tenant, clinicNumber, id],
    queryFn: () => getDetail(tenant, clinicNumber, id),
    enabled: !!tenant && !!clinicNumber && !!id,
  });
};

/**
 * Hook to get all insurer attentions (paginated)
 */
export const useMainInsurerAttentions = (perPage: number = 20, page: number = 1) => {
  const { tenant, clinicNumber } = useClinicContext();

  return useQuery({
    queryKey: ['insurer-attentions', 'list', tenant, clinicNumber, perPage, page],
    queryFn: () => getAll(tenant, clinicNumber, perPage, page),
    enabled: !!tenant && !!clinicNumber,
  });
};

/**
 * Hook to download attachment
 */
export const useMainInsurerAttentionDownload = () => {
  const { tenant, clinicNumber } = useClinicContext();

  return useMutation({
    mutationFn: ({
      attachment,
      id,
      filename,
    }: {
      attachment: string;
      id: number;
      filename: string;
    }) => downloadAttachment(tenant, clinicNumber, attachment, id, filename),
  });
};
